import { Shield, Bell, Globe, Key, Database, Palette, Save, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const settingSections = [
  { id: 'general', icon: Globe, label: 'General', settings: [
    { key: 'org_name', label: 'Organization Name', value: 'Meridian Global', type: 'text' },
    { key: 'org_env', label: 'Environment', value: 'Production', type: 'select', options: ['Production', 'Staging', 'Development'] },
    { key: 'timezone', label: 'Timezone', value: 'Europe/Zurich', type: 'text' },
    { key: 'date_format', label: 'Date Format', value: 'YYYY-MM-DD HH:mm UTC', type: 'text' },
  ]},
  { id: 'security', icon: Shield, label: 'Security', settings: [
    { key: 'mfa', label: 'Require MFA for all users', value: true, type: 'toggle' },
    { key: 'session_timeout', label: 'Session timeout (minutes)', value: '30', type: 'text' },
    { key: 'ip_allowlist', label: 'IP allowlist enabled', value: true, type: 'toggle' },
    { key: 'audit_logging', label: 'Enhanced audit logging', value: true, type: 'toggle' },
  ]},
  { id: 'notifications', icon: Bell, label: 'Notifications', settings: [
    { key: 'email_critical', label: 'Email on critical events', value: true, type: 'toggle' },
    { key: 'email_weekly', label: 'Weekly digest report', value: true, type: 'toggle' },
    { key: 'slack_alerts', label: 'Slack integration alerts', value: false, type: 'toggle' },
    { key: 'soc_escalation', label: 'SOC auto-escalation notifications', value: true, type: 'toggle' },
  ]},
  { id: 'data', icon: Database, label: 'Data & Retention', settings: [
    { key: 'retention_default', label: 'Default retention period', value: '7 years', type: 'text' },
    { key: 'auto_archive', label: 'Auto-archive expired records', value: true, type: 'toggle' },
    { key: 'encryption_at_rest', label: 'Encryption at rest (AES-256)', value: true, type: 'toggle' },
    { key: 'backup_frequency', label: 'Backup frequency', value: 'Every 6 hours', type: 'text' },
  ]},
];

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState('general');
  const [saved, setSaved] = useState(false);
  const [toggles, setToggles] = useState(() => {
    const t = {};
    settingSections.forEach(s => s.settings.forEach(setting => {
      if (setting.type === 'toggle') t[setting.key] = setting.value;
    }));
    return t;
  });

  const section = settingSections.find(s => s.id === activeSection);

  const handleToggle = (key) => {
    setToggles(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    setSaved(true);
    toast.success('Settings saved successfully');
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div data-testid="settings-page" className="space-y-4">
      <div className="text-sm text-[#94A3B8]">Platform configuration and preferences.</div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Section nav */}
        <div className="glass-card p-3 space-y-1">
          {settingSections.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${activeSection === s.id ? 'bg-[#6366F1]/10 text-[#6366F1]' : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#0F1629]'}`}
              >
                <Icon size={16} />{s.label}
              </button>
            );
          })}
        </div>
        {/* Settings panel */}
        <div className="lg:col-span-3 glass-card p-6">
          {section && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <section.icon size={18} className="text-[#6366F1]" />
                <h3 className="text-base font-semibold text-[#F8FAFC]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{section.label}</h3>
              </div>
              <div className="space-y-4">
                {section.settings.map(setting => (
                  <div key={setting.key} className="flex items-center justify-between p-4 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                    <div>
                      <div className="text-sm text-[#F8FAFC]">{setting.label}</div>
                    </div>
                    {setting.type === 'toggle' ? (
                      <div onClick={() => handleToggle(setting.key)} className={`w-10 h-5 rounded-full transition-all cursor-pointer flex items-center ${toggles[setting.key] ? 'bg-[#6366F1] justify-end' : 'bg-[#1E2A45] justify-start'}`}>
                        <div className="w-4 h-4 rounded-full bg-white mx-0.5" />
                      </div>
                    ) : setting.type === 'select' ? (
                      <select className="px-3 py-1.5 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#94A3B8] focus:outline-none focus:border-[#6366F1]" defaultValue={setting.value}>
                        {setting.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type="text" defaultValue={setting.value} className="px-3 py-1.5 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#94A3B8] w-48 text-right focus:outline-none focus:border-[#6366F1]" />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={handleSave} data-testid="save-settings" className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
                  {saved ? <><CheckCircle2 size={14} /> Saved</> : <><Save size={14} /> Save Changes</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Platform Architecture */}
      <div className="glass-card p-6 mt-6" data-testid="platform-architecture">
        <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-[0.15em] mb-1">Platform Architecture</div>
        <div className="text-[10px] text-[#64748B] mb-5">Temporal AI Governance · Proof Infrastructure</div>
        <div className="space-y-5">
          {[
            { name: 'Chain Integrity Engine', desc: 'Validates proof continuity across the CER → CSS → DER → OER → Package chain. Recalculates proof grade when new corroboration is received. Propagates integrity degradation when a chain node is compromised. Evaluates chain restorability and generates remediation requirements.', status: 'ACTIVE · Last run: 4 min ago' },
            { name: 'Temporal Reconstruction Engine', desc: 'Reconstructs governance truth at any point in time by replaying evidence fabric state at a specified timestamp. Rebuilds historical control states from signed records. Enables temporal querying across all governance dimensions.', status: 'ACTIVE · Reconstruction ready' },
            { name: 'Proof Graph Service', desc: 'Manages linked governance objects and their dependency chains. Validates hash links between CER, CSS, DER, OER, and Package records. Maintains immutable evidence lineage. Tracks chain degradation history and restoration events. At runtime, the Proof Graph Service maintains a directed acyclic graph of governance relationships — traversable at any historical timestamp.', status: 'ACTIVE · 47,832 nodes · 4 active chains' },
          ].map(sub => (
            <div key={sub.name}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-[#F8FAFC]">{sub.name}</div>
                <span className="text-[8px] font-mono text-[#10B981]">◆ {sub.status}</span>
              </div>
              <div className="text-[10px] text-[#94A3B8] leading-relaxed mt-1">{sub.desc}</div>
            </div>
          ))}
          <div className="pt-4 border-t border-[#1E2A45]">
            <div className="text-sm font-semibold text-[#F8FAFC] mb-1">Evidence Fabric</div>
            <div className="text-[10px] font-mono text-[#64748B]">47,832 signed records · 5 active workflows · eu-west-1 primary · us-east-1 replica</div>
            <div className="text-[10px] font-mono text-[#64748B]">Last integrity check: 4 minutes ago · Next: automatic · Status: <span className="text-[#10B981]">◆ HEALTHY</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
